'use client';

import { QueryKeys } from '@/constants/query-keys';
import { AnalyticsView } from '@/enums/analytics-type';
import { AuthenticationStatus } from '@/enums/authentication-status';
import { useAnalyticsDataLayer, useDataAnalytics } from '@/hooks/analytics/analytics';
import { AnalyticsBaseType } from '@/types/analytics';
import { RequiredQuery } from '@/types/page-view-tracker';
import { hashKey, useQueryClient } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useEffect, useRef, useState } from 'react';

type PageViewTrackerProps = {
  children: React.ReactNode;
  dataNotInDataLayer?: Record<string, AnalyticsBaseType>;
  eventName: AnalyticsView;
  pageName: string;
  requiredQueries?: RequiredQuery[];
};

const EMPTY_DATA = {};

export const PageViewTracker = ({
  children,
  dataNotInDataLayer = EMPTY_DATA,
  eventName,
  pageName,
  requiredQueries = [],
}: PageViewTrackerProps): React.JSX.Element => {
  const { trackView } = useDataAnalytics();
  useAnalyticsDataLayer();
  const queryClient = useQueryClient();
  const { data: session, status } = useSession();
  const hasTracked = useRef(false);
  const [requiredQueryVersion, setRequiredQueryVersion] = useState(0);
  const isAuthenticated = status === AuthenticationStatus.AUTHENTICATED && !!session;
  const profileQueryHash = hashKey(QueryKeys.user.profile);
  const isProfileDeclaredByPage = requiredQueries.some(({ queryKey }) => hashKey(queryKey) === profileQueryHash);
  const shouldWaitForProfile = isAuthenticated && !isProfileDeclaredByPage;

  useEffect(() => {
    if (!requiredQueries.length && !shouldWaitForProfile) return;

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (hasTracked.current || event.type !== 'updated') return;

      const isTrackedQuery = shouldWaitForProfile && event.query.queryHash === profileQueryHash ||
        requiredQueries.some(({ queryKey }) => hashKey(queryKey) === event.query.queryHash);

      if (isTrackedQuery) {
        setRequiredQueryVersion(v => v + 1);
      }
    });

    return unsubscribe;
  }, [profileQueryHash, queryClient, requiredQueries, shouldWaitForProfile]);

  useEffect(() => {
    if (status === AuthenticationStatus.LOADING) {
      return;
    }

    const isProfileQueryReady  = !shouldWaitForProfile ||
      queryClient.getQueryState(QueryKeys.user.profile)?.status === 'success';

    const arePageRequiredQueriesReady = requiredQueries.every(({ queryKey, required }) => {
      const isRequired = typeof required === 'function'
        ? required({ isAuthenticated })
        : required;

      if (!isRequired) {
        return true;
      }

      const queryState = queryClient.getQueryState(queryKey);

      return queryState?.status === 'success';
    });

    if (isProfileQueryReady  && arePageRequiredQueriesReady && !hasTracked.current) {
      trackView(pageName, eventName, dataNotInDataLayer);
      hasTracked.current = true;
    }
  }, [
    dataNotInDataLayer,
    eventName,
    isAuthenticated,
    pageName,
    profileQueryHash,
    queryClient,
    requiredQueries,
    requiredQueryVersion,
    shouldWaitForProfile,
    status,
    trackView,
  ]);

  return <>{children}</>;
};
